import * as React from 'react';

type Props = {
  oldJson: object;
  newJson: object;
};

// A component that displays a diff of two JSON objects, with nice indent, no dependency
export function JsonDiffView({ oldJson, newJson }: Props) {
  const renderDiff = (obj1: any, obj2: any, indent: number = 0): JSX.Element => {
    const lines: JSX.Element[] = [];
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
    const keysArray = Array.from(allKeys);
    const isArray1 = Array.isArray(obj1);
    const isArray2 = Array.isArray(obj2);
    const isArray = isArray1 || isArray2;

    keysArray.forEach((key, index) => {
      const val1 = obj1[key];
      const val2 = obj2[key];
      const padding = ' '.repeat(indent * 2);
      const isLast = index === keysArray.length - 1;
      const comma = isLast ? '' : ',';

      // For arrays, don't show the key (index)
      const keyDisplay = isArray ? '' : `"${key}": `;

      if (!(key in obj2)) {
        // Deleted key
        lines.push(
          <div key={`${key}-deleted`} style={{ color: '#ff0000', backgroundColor: '#ffecec', whiteSpace: 'pre' }}>
            {padding}- {keyDisplay}
            {JSON.stringify(val1)}
            {comma}
          </div>,
        );
      } else if (!(key in obj1)) {
        // Added key
        lines.push(
          <div key={`${key}-added`} style={{ color: '#008000', backgroundColor: '#eaffea', whiteSpace: 'pre' }}>
            {padding}+ {keyDisplay}
            {JSON.stringify(val2)}
            {comma}
          </div>,
        );
      } else if (typeof val1 === 'object' && typeof val2 === 'object' && val1 !== null && val2 !== null) {
        // Nested objects
        const nestedIsArray = Array.isArray(val1) || Array.isArray(val2);
        lines.push(
          <div key={`${key}-object`} style={{ whiteSpace: 'pre' }}>
            {padding}
            {keyDisplay}
            {nestedIsArray ? '[' : '{'}
            {renderDiff(val1, val2, indent + 1)}
            {padding}
            {nestedIsArray ? ']' : '}'}
            {comma}
          </div>,
        );
      } else if (val1 !== val2) {
        // Changed value
        lines.push(
          <div key={`${key}-changed`}>
            <div style={{ color: '#ff0000', backgroundColor: '#ffecec', whiteSpace: 'pre' }}>
              {padding}- {keyDisplay}
              {JSON.stringify(val1)}
              {comma}
            </div>
            <div style={{ color: '#008000', backgroundColor: '#eaffea', whiteSpace: 'pre' }}>
              {padding}+ {keyDisplay}
              {JSON.stringify(val2)}
              {comma}
            </div>
          </div>,
        );
      } else {
        // Unchanged
        lines.push(
          <div key={`${key}-unchanged`} style={{ whiteSpace: 'pre' }}>
            {padding}
            {keyDisplay}
            {JSON.stringify(val1)}
            {comma}
          </div>,
        );
      }
    });

    return <>{lines}</>;
  };

  const isRootArray = Array.isArray(oldJson) || Array.isArray(newJson);

  return (
    <pre style={{ fontFamily: 'monospace', whiteSpace: 'pre', margin: 0 }}>
      {isRootArray ? '[' : '{'}
      {renderDiff(oldJson, newJson, 1)}
      {isRootArray ? ']' : '}'}
    </pre>
  );
}
